import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { ApiError } from '../api/client'
import { useChangeHandle, useFriends, useGroups, useMyProfile, useSignOut } from '../api/hooks'
import type {
  FriendListItem,
  GroupListItem,
  ProfileGameRatings,
  ProfileRatingScope,
  ProfileRecentPlay,
} from '../api/types'
import { ScreenShell } from '../components/ScreenShell'
import { formatDateShort } from '../datetime'
import {
  GameThumb,
  MeepleAvatar,
  NumberCircle,
  Pill,
  RatingAxis,
  RatingGaussian,
  RatingSummary,
  ScorePad,
  ScorePadRow,
  SectionHeader,
  TopBar,
  ratingDomain,
} from '../primitives'

export const ProfileScreen = () => {
  const profile = useMyProfile()
  const groups = useGroups()
  const friends = useFriends()

  if (profile.isLoading) {
    return (
      <div className="paper" style={{ minHeight: '100vh', padding: 16, color: 'var(--ink-3)' }}>
        Loading…
      </div>
    )
  }
  if (!profile.data) return null
  const { user, ratings, recentPlays, totals } = profile.data

  return (
    <ScreenShell active="me">
      <TopBar
        title="Profile"
        right={
          <Link
            to="/account"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 12,
              color: 'var(--ink-2)',
              textDecoration: 'none',
              padding: '6px 10px',
              border: '1px solid var(--line-soft)',
              borderRadius: 4,
            }}
          >
            Account
          </Link>
        }
      />

      <div style={{ padding: '18px 16px 6px', display: 'flex', gap: 14, alignItems: 'center' }}>
        <MeepleAvatar
          name={user.displayName}
          hue={user.avatarHue}
          shape={user.avatarShape}
          size={72}
        />
        <div style={{ flex: 1 }}>
          <div className="kicker">you</div>
          <div
            style={{
              fontFamily: 'var(--font-serif)',
              fontStyle: 'italic',
              fontSize: 30,
              lineHeight: 1.05,
              color: 'var(--ink)',
            }}
          >
            {user.displayName}
          </div>
          <HandleEditor currentHandle={user.handle} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, padding: '8px 16px 4px' }}>
        <Stat label="games rated" value={totals.gamesRated} />
        <Stat label="recent plays" value={totals.playsRecorded} />
        <Stat label="ranked" value={totals.nonProvisionalRatings} />
      </div>

      <GroupsSection groups={groups.data} loading={groups.isLoading} />

      <FriendsSection friends={friends.data?.accepted} loading={friends.isLoading} />

      <SectionHeader kicker="ratings" title="Per-game" />
      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {ratings.length === 0 && (
          <div className="felt-card" style={{ padding: 14, color: 'var(--ink-2)' }}>
            <div style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 18 }}>
              No ratings yet.
            </div>
            <div style={{ marginTop: 4, fontSize: 13 }}>
              Record a play to start collecting them.
            </div>
          </div>
        )}
        {ratings.map((r) => (
          <GameRatingsCard key={r.game.id} ratings={r} />
        ))}
      </div>

      <SectionHeader kicker="recent" title="Plays" />
      <div style={{ padding: '0 16px' }}>
        {recentPlays.length === 0 ? (
          <div className="felt-card" style={{ padding: 14, color: 'var(--ink-2)' }}>
            No plays yet.
          </div>
        ) : (
          <ScorePad>
            {recentPlays.map((p) => (
              <RecentPlayRow key={p.playId} play={p} />
            ))}
          </ScorePad>
        )}
      </div>

      <div style={{ padding: '24px 16px 8px', display: 'flex', justifyContent: 'center' }}>
        <SignOutButton />
      </div>
    </ScreenShell>
  )
}

const SignOutButton = () => {
  const signOut = useSignOut()
  const onClick = async () => {
    try {
      await signOut.mutateAsync()
    } catch {
      // ignore — proceed to sign-in regardless so a stale session can't trap the user
    }
    window.location.href = '/sign-in'
  }
  return (
    <button
      type="button"
      className="btn-ghost"
      onClick={onClick}
      disabled={signOut.isPending}
      style={{ fontSize: 13, padding: '6px 14px' }}
    >
      {signOut.isPending ? 'Signing out…' : 'Sign out'}
    </button>
  )
}

const HandleEditor = ({ currentHandle }: { currentHandle: string }) => {
  const change = useChangeHandle()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(currentHandle)
  const [error, setError] = useState<string | null>(null)

  if (!editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>@{currentHandle}</div>
        <button
          type="button"
          className="btn-ghost"
          onClick={() => {
            setDraft(currentHandle)
            setError(null)
            setEditing(true)
          }}
          style={{ fontSize: 11, padding: '2px 8px' }}
        >
          Change
        </button>
      </div>
    )
  }

  const onSave = async () => {
    setError(null)
    if (draft.trim() === currentHandle) {
      setEditing(false)
      return
    }
    try {
      await change.mutateAsync(draft.trim())
      setEditing(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.friendlyMessage : 'Could not change')
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>@</span>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={20}
          autoFocus
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            padding: '2px 6px',
            border: '1px solid var(--line-soft)',
            borderRadius: 3,
            width: 140,
          }}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
        />
        <button
          type="button"
          className="btn-stamp"
          onClick={onSave}
          disabled={change.isPending}
          style={{ fontSize: 11, padding: '2px 8px' }}
        >
          {change.isPending ? '…' : 'Save'}
        </button>
        <button
          type="button"
          className="btn-ghost"
          onClick={() => {
            setEditing(false)
            setError(null)
          }}
          style={{ fontSize: 11, padding: '2px 8px' }}
        >
          Cancel
        </button>
      </div>
      {error && <div style={{ fontSize: 11, color: 'var(--terra-ink)' }}>{error}</div>}
    </div>
  )
}

const GroupsSection = ({
  groups,
  loading,
}: {
  groups: GroupListItem[] | undefined
  loading: boolean
}) => (
  <>
    <SectionHeader
      kicker="memberships"
      title="Groups"
      action={
        <Link
          to="/group/new"
          style={{
            fontSize: 12,
            color: 'var(--terra)',
            textDecoration: 'underline',
            textUnderlineOffset: 2,
          }}
        >
          + New group
        </Link>
      }
    />
    <div style={{ padding: '0 16px' }}>
      {loading && (
        <div className="felt-card" style={{ padding: 14, color: 'var(--ink-3)', fontSize: 13 }}>
          Loading…
        </div>
      )}
      {groups && groups.length === 0 && (
        <div className="felt-card" style={{ padding: 14, color: 'var(--ink-2)', fontSize: 13 }}>
          Not in any groups yet.
        </div>
      )}
      {groups && groups.length > 0 && (
        <ScorePad>
          {groups.map((g) => (
            <ScorePadRow key={g.id}>
              <Link
                to="/group/$id"
                params={{ id: g.id }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>{g.name}</div>
                {g.description && (
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--ink-3)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {g.description}
                  </div>
                )}
              </Link>
              {g.role === 'admin' && <Pill color="gilt">admin</Pill>}
              <span style={{ color: 'var(--ink-3)' }}>→</span>
            </ScorePadRow>
          ))}
        </ScorePad>
      )}
    </div>
  </>
)

const FRIENDS_SHOWN = 6

const FriendsSection = ({
  friends,
  loading,
}: {
  friends: FriendListItem[] | undefined
  loading: boolean
}) => {
  const total = friends?.length ?? 0
  const shown = friends?.slice(0, FRIENDS_SHOWN) ?? []
  const hasMore = total > shown.length
  const footerLabel = hasMore ? `All ${total} friends` : 'Manage friends'

  return (
    <>
      <SectionHeader
        kicker="people"
        title="Friends"
        action={
          <Link
            to="/friends"
            style={{
              fontSize: 12,
              color: 'var(--terra)',
              textDecoration: 'underline',
              textUnderlineOffset: 2,
            }}
          >
            Find friends
          </Link>
        }
      />
      <div style={{ padding: '0 16px' }}>
        {loading && (
          <div className="felt-card" style={{ padding: 14, color: 'var(--ink-3)', fontSize: 13 }}>
            Loading…
          </div>
        )}
        {!loading && total === 0 && (
          <Link
            to="/friends"
            className="felt-card"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            <div style={{ flex: 1, fontSize: 13, color: 'var(--ink-2)' }}>No friends yet.</div>
            <span style={{ color: 'var(--terra)', fontSize: 18, fontWeight: 600 }}>→</span>
          </Link>
        )}
        {!loading && total > 0 && (
          <div className="felt-card" style={{ padding: 0 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                padding: '4px 0',
              }}
            >
              {shown.map((f) => (
                <Link
                  key={f.user.id}
                  to="/u/$handle"
                  params={{ handle: f.user.handle }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 10px',
                    color: 'inherit',
                    textDecoration: 'none',
                    minWidth: 0,
                  }}
                >
                  <MeepleAvatar
                    name={f.user.displayName}
                    hue={f.user.avatarHue}
                    shape={f.user.avatarShape}
                    size={28}
                  />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {f.user.displayName}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: 'var(--ink-3)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      @{f.user.handle}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <Link
              to="/friends"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                color: 'var(--ink-2)',
                textDecoration: 'none',
                fontSize: 13,
                borderTop: '1px solid var(--line-soft)',
              }}
            >
              <span>{footerLabel}</span>
              <span style={{ color: 'var(--terra)', fontSize: 18, fontWeight: 600 }}>→</span>
            </Link>
          </div>
        )}
      </div>
    </>
  )
}

const Stat = ({ label, value }: { label: string; value: number }) => (
  <div
    className="felt-card"
    style={{
      flex: 1,
      padding: '8px 10px',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    }}
  >
    <div className="kicker">{label}</div>
    <div
      style={{
        fontFamily: 'var(--font-mono)',
        fontWeight: 600,
        fontSize: 22,
        color: 'var(--ink)',
        lineHeight: 1,
      }}
    >
      {value}
    </div>
  </div>
)

const GameRatingsCard = ({ ratings }: { ratings: ProfileGameRatings }) => {
  return (
    <div
      className="felt-card"
      style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <GameThumb label={ratings.game.name} imageUrl={ratings.game.imageUrl} size={42} />
        <div style={{ flex: 1, fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16 }}>
          {ratings.game.name}
        </div>
      </div>

      {ratings.crossGroup && (
        <RatingScopeRow label="Cross-group" subLabel="private to you" scope={ratings.crossGroup} />
      )}
      {ratings.perGroup.map((s) => (
        <RatingScopeRow key={s.groupId} label={s.groupName ?? '—'} scope={s} />
      ))}
    </div>
  )
}

const RatingScopeRow = ({
  label,
  subLabel,
  scope,
}: {
  label: string
  subLabel?: string
  scope: ProfileRatingScope
}) => {
  const trend = scope.history.map((h) => h.mu)
  const lastRawDelta = trend.length >= 2 ? trend[trend.length - 1]! - trend[trend.length - 2]! : 0
  const domain = ratingDomain(scope.mu, scope.sigma)
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        paddingTop: 8,
        borderTop: '1.5px dashed var(--paper-edge)',
      }}
    >
      <div>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
        {subLabel && <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>{subLabel}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 10 }}>
        <RatingSummary
          tier={scope.tier}
          rawMu={scope.mu}
          playCount={scope.playCount}
          rawDelta={lastRawDelta}
        />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <RatingGaussian
            mu={scope.mu}
            sigma={scope.sigma}
            domain={domain}
            width={260}
            height={56}
          />
          <RatingAxis xMin={domain[0]} xMax={domain[1]} width={260} />
        </div>
      </div>
    </div>
  )
}

const RecentPlayRow = ({ play }: { play: ProfileRecentPlay }) => (
  <ScorePadRow>
    <NumberCircle n={play.rank} winner={play.rank === 1} size={22} />
    <GameThumb label={play.game.name} imageUrl={play.game.imageUrl} size={32} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <Link
        to="/play/$id"
        params={{ id: play.playId }}
        style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}
      >
        {play.game.name}
      </Link>
      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
        {play.groupName ?? '(no group)'} · {formatDateShort(play.playedAt)}
      </div>
    </div>
    <span style={{ color: 'var(--ink-3)' }}>→</span>
  </ScorePadRow>
)
